import ChatIcon from '@mui/icons-material/Chat'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import VerifiedIcon from '@mui/icons-material/Verified'
import { Box, Card, CardContent, Chip, Divider, Grid2 as Grid, Stack, Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import { useAtomValue } from 'jotai'
import * as React from 'react'
import { memo, useCallback, useEffect, useState } from 'react'
import { shallowEqual } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import Config from '../../data/Config'
import { isAuthenticatedAtom } from '../../domains/auth/AtomAuth'
import { AvatarPlus } from '../../utils/gravatar'
import BioCard from '../cards/BioCard'
import CButton from '../common/CButton'
import ShareButton from '../common/ShareButton'

const UserCard = ({ user, size = 'small', clickable = true, bio = true, collapsibleBio = false, shareButton = false, chatButton = true, sx = {} }) => {
  const navigate = useNavigate()
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const location = useLocation()
  const [isFocusMode, setIsFocusMode] = useState(false)
  const [bioExpanded, setBioExpanded] = useState(true)

  // 检查URL是否包含focus=true参数
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    setIsFocusMode(searchParams.get('focus') === 'true')
  }, [location.search])

  const resourceId = user?.resourceId || user?.value || user?.id
  const isInstructorDetailView = user?.roles?.includes('INSTRUCTOR')
  const path = isInstructorDetailView ? `/instructors/${resourceId}` : `/users/${resourceId}`

  const handleClick = useCallback(() => {
    if (!clickable) return
    navigate(path)
  }, [])

  const toggleBio = useCallback((event) => {
    event.stopPropagation()
    setBioExpanded((prev) => !prev)
  }, [])

  const handleMessageClick = async () => {
    if (!isAuthenticated) {
      return
    }

    try {
      // Create or get room for this instructor
      const ChatService = (await import('../../services/ChatService')).default
      const roomData = await ChatService.getOrCreateRoom(user.resourceId)

      // Navigate to chat page with roomId
      navigate(`/chat?roomId=${roomData.roomId}`)
    } catch (error) {
      console.error('Failed to create/get room:', error)
      // Still navigate to chat page, it will handle the error
      navigate('/chat')
    }
  }

  // 处理退出焦点模式
  const handleExitFocus = useCallback(
    (event) => {
      event.stopPropagation() // 防止触发卡片的点击事件

      // 创建新的URL，移除focus参数
      const url = new URL(window.location.href)
      url.searchParams.delete('focus')

      // 使用navigate跳转到无参数的URL
      navigate(url.pathname + url.search)
    },
    [navigate],
  )

  if (!user || !user.name) {
    return null
  }

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: !clickable ? 'default' : 'pointer',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 6,
          transform: !clickable ? 'none' : 'scale(1.01)',
          transition: 'all 0.2s ease-in-out',
        },
        position: 'relative',
        ...sx,
      }}
    >
      {/* Buttons container */}
      {shareButton && (
        <Box alignItems="center" justifyContent="center" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, display: 'flex', gap: 0 }}>
          {/* Exit Focus Mode Button - only show when in focus mode */}
          {/* Disable this button for now */}
          {false && isFocusMode && (
            <CButton title="Exit focus mode" onClick={handleExitFocus} size="small">
              <FullscreenExitIcon fontSize="medium" />
            </CButton>
          )}

          {/* Share Button */}
          {
            <>
              <ShareButton
                title={`Instructor ${user.name}'s profile`}
                text={`Instructor ${user.name}'s profile on BookingOnSnow!`}
                url={window.location.href + '?focus=true'}
                size="small"
              />
            </>
          }
        </Box>
      )}

      <CardContent
        sx={{
          py: {
            xs: size === 'large' ? 2 : 1,
            sm: size === 'large' ? 2.5 : 1,
            md: size === 'large' ? 3 : 1,
          },
          px: {
            xs: size === 'large' ? 0.5 : 0.5,
            sm: size === 'large' ? 1.5 : 0.5,
            md: size === 'large' ? 3 : 0.5,
          },
        }}
      >
        {/* <CardContent sx={{ p: size === 'large' ? 3 : 2 }}> */}
        <Stack spacing={size === 'large' ? 0 : 2} sx={{ p: size === 'large' ? 2 : 2 }}>
          <Grid>
            <AvatarPlus user={user} size={size} />
          </Grid>

          {user.certificates?.length > 0 && (
            <Grid sx={{ p: size === 'large' ? 3 : 2 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                <Typography variant={size === 'large' ? 'subtitle1' : 'subtitle2'} color="text.secondary" sx={{ mb: 0 }}>
                  Certifications:
                </Typography>
                {user.certificates.map((cert) => (
                  <Chip key={cert} label={cert} variant="outlined" size={size === 'large' ? 'medium' : 'small'} />
                ))}
                {user.certificateVerified && <Chip icon={<VerifiedIcon />} label="Verified" color="success" size={size === 'large' ? 'medium' : 'small'} />}
              </Stack>
            </Grid>
          )}

          {Config.CHAT_ENABLED && chatButton && size === 'large' && (
            <Grid sx={{ p: size === 'large' ? 3 : 2 }}>
              <Button size="small" color="primary" variant="outlined" onClick={handleMessageClick} startIcon={<ChatIcon />}>
                Chat with me
              </Button>
            </Grid>
          )}

          {bio && user.bio && (
            <>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                {collapsibleBio && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                    <Button size="Large" color="primary" aria-expanded={bioExpanded} aria-label={bioExpanded ? 'Collapse biography' : 'Expand biography'} onClick={toggleBio}>
                      <Typography variant={size === 'large' ? 'h6' : 'subtitle1'}>About me</Typography>
                      {bioExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Button>
                  </Box>
                )}

                <Collapse in={bioExpanded}>
                  <Grid container justifyContent="center" alignItems="center" maxWidth={size === 'large' ? 800 : 300}>
                    <Grid size={{ xs: 12, sm: 12, md: size === 'large' ? 12 : 12 }}>
                      <BioCard bio={user.bio} variant={size === 'large' ? 'expanded' : 'compact'} />
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default memo(UserCard, shallowEqual)
